import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { MRT_SortingState } from "material-react-table";
import { mrtSortingStateToObject } from "src/api/apiHelpers";

import { z } from "zod";
import { zodParseWithLog } from "src/api/apiErrorLogs";

type Options = {
    page?: number;
    itemsPerPage?: number;
    search?: string;
    email?: string;
    companyName?: string;
    companyId?: number;
    sorting?: MRT_SortingState;
    enabled?: boolean;
};
type ParamsType = {
    params: {
        page: number;
        itemsPerPage: number;
        q: string;
        email: string;
        "organizations.name"?: string;
        "organizations.id"?: number;
        order?: Record<string, string>;
    };
};

export const useAllUsers = (options?: Options) => {
    const {
        page = 1,
        itemsPerPage = 200,
        search = "",
        email = "",
        companyName = "",
        companyId,
        sorting = [],
        enabled = true,
    } = options || {};
    const params: ParamsType = {
        params: {
            page,
            itemsPerPage,
            q: search,
            email,
            order: mrtSortingStateToObject(sorting),
        },
    };

    if (companyName) {
        params.params["organizations.name"] = companyName;
    }

    if (companyId) {
        params.params["organizations.id"] = companyId;
    }

    const query = useQuery({
        queryKey: ["allUsers", page, itemsPerPage, search, email, companyName, companyId, sorting],
        enabled,
        queryFn: async () => {
            return await loggedInApiCall.get("/api/users", params);
        },
        select: (request) => {
            const totalItems = request.data["hydra:totalItems"];
            const user = request.data["hydra:member"];

            const data = zodParseWithLog(user, UserSchemaZod.array(), "allUsers");

            data.forEach((user) => {
                user.roles = user.roles.filter((role) => role !== "ROLE_USER");
            });

            return {
                items: data,
                totalItems: totalItems,
            };
        },
    });

    return {
        users: query.data?.items,
        totalItems: query.data?.totalItems,
        ...query,
    };
};

export type UserCompanySector = {
    id: number;
    name: string;
    ssqId?: number;
};
const UserCompanySectorZod: z.ZodSchema<UserCompanySector> = z.object({
    id: z.number(),
    name: z.string(),
    ssqId: z
        .number()
        .nullish()
        .transform((value) => value ?? undefined),
});

export type UserCompany = {
    "@id": IRI;
    id: number;
    name: string;
    organizationSector?: UserCompanySector;
};
export type UserOrganization = Omit<UserCompany, "@id"> & {
    "@id"?: IRI;
    roles: string[];
};
const UserOrganizationZod: z.ZodSchema<UserOrganization> = z.object({
    "@id": z.string().optional(),
    id: z.number(),
    name: z.string(),
    organizationSector: UserCompanySectorZod.optional(),
    roles: z.array(z.string()),
});

export type User = {
    "@id": IRI;
    id: number;
    email: string;
    firstname?: string;
    lastname?: string;
    phone?: string;
    organizations?: UserOrganization[];
    roles: Array<string>;
    canView: boolean;
    canModify: boolean;
    canDelete: boolean;
    canImpersonate?: boolean;
    is_verified: boolean;
    jsonAttributes?: Record<string, any>;
};

export const UserSchemaZod: z.ZodSchema<User> = z.object({
    "@id": z.string(),
    id: z.number(),
    email: z.string(),
    firstname: z.string().optional(),
    lastname: z.string().optional(),
    phone: z.string().optional(),
    organizations: UserOrganizationZod.array().optional(),
    roles: z.array(z.string()),
    canView: z.boolean(),
    canModify: z.boolean(),
    canDelete: z.boolean(),
    canImpersonate: z.boolean().optional(),
    is_verified: z.boolean(),
    jsonAttributes: z.record(z.string(), z.any()).optional(),
});
